<template>
  <v-container class="meal">
    <div
      class="meal--photo"
      @click="showPicture"
      @mouseover="mealOpenHover"
      @mouseleave="mealClearHover"
    >
      <span v-if="picture && !pictureResource" class="meal--photo-loading">
        <v-progress-circular color="#bed1ff" indeterminate size="40" />
      </span>
      <v-progress-linear
        v-if="hoverLoader"
        background-color="rgba(255,255,255,0.3)"
        class="meal--photo-hover-loader"
        :value="hoverLoader"
      ></v-progress-linear>
      <img v-if="picture" class="meal--photo-clickable" :src="picture" />
      <img
        v-else-if="isResourceThumbnail"
        class="meal--photo-clickable"
        :src="
          pictureResource.thumbnailUrl ||
            pictureResource.previewUrl ||
            pictureResource.fileUrl
        "
      />
      <img
        v-else-if="pictureResource && pictureResource.fileTypeId"
        :src="placeholder(pictureResource.fileTypeId)"
      />
      <img v-else src="@/assets/meal_placeholder.png" />

      <v-icon
        size="32"
        color="bhWhite"
        v-if="pictureResource && pictureResource.fileTypeId == 19"
        >bhi-play-circle</v-icon
      >
    </div>
    <div class="meal--desc">
      <div class="meal--desc-comments">
        <p
          class="comment"
          v-for="comment in comments"
          :key="comment.id"
          :class="
            comment.createdById == currentPatient.profileId
              ? 'participants'
              : ''
          "
        >
          <span v-if="!comment.deleted">{{ comment.comment }}</span>
          <i class="edited" v-if="comment.edited">edited</i>
          <i class="edited" v-if="comment.deleted">comment deleted</i>
        </p>
      </div>
      <div class="meal-comment--wrap">
        <p v-if="comment && !isEditing && !showCommentInput">
          <img src="@/assets/comment.png" />
          <span>{{ comment }}</span>
          <v-icon
            v-if="commentEditable"
            class="meal-comment__icon--editing"
            v-on:click="toggleEditMealComment"
            color="accent"
            :class="!isEditing ? 'd-inline' : 'd-none'"
          >
            bhi-pencil
          </v-icon>
        </p>
        <v-textarea
          v-else
          v-model="mealComment"
          ref="textField"
          class="meal-comment rounded-pill"
          placeholder="comment for this entry"
          background-color="bhWhite"
          elevation="0"
          flat
          dense
          solo
          filled
          hide-details
          full-width
          no-resize
          rows="1"
          row-height="10"
          auto-grow
          @input="onInput"
          @keydown.enter="addMealComment"
          @blur="onBlur"
        >
          <template v-slot:prepend-inner>
            <v-btn icon color="accent" @mousedown="showEmojiHandler">
              <v-icon> mdi-emoticon-outline </v-icon>
            </v-btn>
          </template>
          <template v-slot:append-outer>
            <v-btn
              icon
              color="accent"
              @click="addMealComment"
              :disabled="!mealComment"
            >
              <v-icon> mdi-arrow-up </v-icon>
            </v-btn>
          </template>
          <template v-slot:append>
            <v-btn icon color="accent" @click="cancelMealComment">
              <v-icon> bhi-close-circle-filled </v-icon>
            </v-btn>
          </template>
        </v-textarea>
        <v-card class="meal-comment--emoji" v-if="showEmoji">
          <v-btn
            text
            v-for="emo in emoji"
            :key="emo.key"
            @mousedown="addEmoji($event, emo.value)"
          >
            {{ emo.value }}
          </v-btn>
        </v-card>
      </div>
    </div>
  </v-container>
</template>
<script>
import { emojiDictionary } from '@/services/MessageService'
import ReactionsMixin from '@/mixins/reactions'
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/helpers/debounce'

export default {
  name: 'Meal',
  components: {},
  mixins: [ReactionsMixin],
  props: {
    comment: {
      type: String,
      default: '',
    },
    commentEditable: {
      type: Boolean,
      default: true,
    },
    comments: {
      type: Array,
      default: function() {
        return []
      },
    },
    picture: {
      type: [String, Number],
      default: '',
    },
    reaction: {
      type: String,
      default: '',
    },
    hoverLoader: {
      type: Number,
      default: 0,
    },
    resourceId: {
      type: [Number, Boolean],
      default: false,
    },
    participantId: {
      type: [String, Number],
      default: '',
    },
    date: {
      type: String,
      default: '',
    },
    mealId: {
      type: [String, Number],
      default: '',
    },
  },
  data: () => {
    return {
      emoji: emojiDictionary,
      showEmoji: false,
      isEditing: false,
      newComment: false,
      newMealComment: '',
      oldMealComment: '',
      showCommentInput: false,
      draftDirty: false,
      pictureResource: null,
    }
  },
  beforeDestroy() {
    if (!this.draftDirty) return
    // flush pending draft when leaving the journal (meal unmounts)
    debounce(null, 0, 'mealCommentDraft-' + this.mealId, 'cancel')
    const value = this.newMealComment
    if (!value) {
      this.deleteMealCommentDraft(this.draftParams())
    } else {
      this.saveMealCommentDraft({ ...this.draftParams(), comment: value })
    }
    this.draftDirty = false
  },
  created() {
    if (this.resourceId) {
      this.getResource({ id: this.resourceId }).then(() => {
        this.pictureResource = { ...this.resource }
      })
    }
    this.loadMealCommentDraft()
  },
  computed: {
    ...mapState('resourcesStates', ['resource', 'loading']),
    ...mapState('patientsStates', ['currentPatient']),
    ...mapState('dictionariesStates', ['dictionaryFileType']),
    reactionComputed() {
      return this.reactions.find(reaction => reaction.title === this.reaction)
    },
    mealComment: {
      get() {
        if (!this.newMealComment && !this.isEditing) return this.comment
        else return this.newMealComment
      },
      set(val) {
        if (!this.oldMealComment) {
          this.oldMealComment = this.comment
        }
        this.newMealComment = val
      },
    },
    updatedMeal() {
      return {
        comment: '',
        title: '',
        reaction: '',
      }
    },
    isResourceThumbnail() {
      const isImageThumbnail =
        this.pictureResource?.fileTypeId == 17 &&
        (this.pictureResource?.thumbnailUrl ||
          this.pictureResource?.previewUrl ||
          this.pictureResource?.fileUrl)
      const isVideoThumbnail =
        this.pictureResource?.fileTypeId == 19 &&
        (this.pictureResource?.thumbnailUrl || this.pictureResource?.previewUrl)
      return !this.picture && (isImageThumbnail || isVideoThumbnail)
    },
  },
  methods: {
    ...mapActions('resourcesStates', ['getResource']),
    ...mapActions('patientsStates', [
      'getMealCommentDraft',
      'saveMealCommentDraft',
      'deleteMealCommentDraft',
    ]),
    draftParams() {
      return {
        id: this.participantId,
        date: this.date,
        mealId: this.mealId,
      }
    },
    loadMealCommentDraft() {
      if (!this.mealId || !this.participantId || !this.date) return
      this.getMealCommentDraft(this.draftParams()).then(draft => {
        if (draft?.comment) {
          this.newMealComment = draft.comment
          this.showCommentInput = true
        }
      })
    },
    saveMealCommentDraftDebounced() {
      this.draftDirty = true
      debounce(
        () => {
          const value = this.newMealComment
          if (!value) {
            this.deleteMealCommentDraft(this.draftParams())
          } else {
            this.saveMealCommentDraft({ ...this.draftParams(), comment: value })
          }
          this.draftDirty = false
        },
        2000,
        'mealCommentDraft-' + this.mealId,
      )
    },
    addMealComment() {
      this.showEmoji = false
      this.$emit('addMealComment', { comment: this.mealComment, edited: false })
      this.isEditing = false
      this.newMealComment = ''
      this.showCommentInput = false
      // cancel pending autosave and remove the stored draft
      debounce(null, 0, 'mealCommentDraft-' + this.mealId, 'cancel')
      this.deleteMealCommentDraft(this.draftParams())
      this.draftDirty = false
    },
    cancelMealComment(comment) {
      this.showEmoji = false
      this.isEditing = false
      if (this.oldMealComment) {
        this.newMealComment = this.oldMealComment
      }
      this.oldMealComment = ''
      this.newMealComment = ''
      this.showCommentInput = false
      // cancel pending autosave and remove the stored draft
      debounce(null, 0, 'mealCommentDraft-' + this.mealId, 'cancel')
      this.deleteMealCommentDraft(this.draftParams())
      this.draftDirty = false
    },
    onInput() {
      this.showEmoji = false
      this.saveMealCommentDraftDebounced()
    },
    onBlur() {
      this.showEmoji = false
    },
    showEmojiHandler() {
      this.showEmoji = true
    },
    toggleEditMealComment() {
      this.newMealComment = this.mealComment
      this.isEditing = !this.isEditing
    },
    addEmoji(e, emoji) {
      e.preventDefault()
      const textField = this.$refs.textField.$refs.input
      const cursorPosition = textField.selectionStart
      const len = this.newMealComment.length
      const before = this.newMealComment.substr(0, cursorPosition)
      const after = this.newMealComment.substr(cursorPosition, len)

      this.newMealComment = before + emoji + after
      this.setCursorPosition(cursorPosition + emoji.length)
      this.saveMealCommentDraftDebounced()
    },
    setCursorPosition(pos) {
      this.$nextTick().then(() => {
        this.$refs.textField.$refs.input.selectionStart = pos
        this.$refs.textField.$refs.input.selectionEnd = pos
      })
    },
    reactionClicked(reaction) {
      this.$emit('updateMealBase', { bhReaction: reaction.title })
    },
    showPicture() {
      const fileTypeId = this.pictureResource.fileTypeId
      let typeName = this.dictionaryFileType[fileTypeId]
      if (typeName === 'txt') return
      if (this.pictureResource) {
        const title =
          this.pictureResource.title.replace(/[/\\?%*:|"<>]/g, '-') +
          '.' +
          this.pictureResource.name
            .split('.')
            .pop()
            .trim()
            .toLowerCase()
        this.$emit('show-bigger-image', {
          url: this.pictureResource.fileUrl,
          file:
            this.pictureResource.previewUrl ||
            this.pictureResource.fileUrl ||
            this.pictureResource.thumbnailUrl,
          name: title,
          mlResourceUrl: this.pictureResource.id,
          type: this.pictureResource.fileTypeId == 19 ? 3 : 1,
        })
      }
    },
    mealOpenHover() {
      if (this.pictureResource)
        this.$emit(
          'mealOpenHover',
          this.pictureResource.id,
          this.pictureResource.thumbnailUrl,
          this.pictureResource.previewUrl,
          this.pictureResource.fileUrl,
          this.pictureResource.fileTypeId,
        )
    },
    mealClearHover() {
      if (this.pictureResource)
        this.$emit('mealClearHover', this.pictureResource.id)
    },
    placeholder(fileTypeId) {
      let image = 'placeholder_note.png'
      switch (fileTypeId) {
        case 17:
          image = 'meal_placeholder.png'
          break
        case 18:
          image = 'placeholder_image.png'
          break
        case 19:
          image = 'placeholder_video.png'
          break
        case 20:
          image = 'placeholder_pdf.png'
          break
        case 21:
          image = 'placeholder_doc.png'
          break
        case 22:
          image = 'placeholder_presentation.png'
          break
        case 23:
          image = 'placeholder_sheet.png'
          break
        default:
          break
      }
      return require('@/assets/' + image)
    },
  },
}
</script>
<style lang="scss">
.meal {
  background: $bh-gray--6;
  border-radius: 8px;
  padding: 0;
  display: flex;
  flex-wrap: nowrap;
  min-height: 128px;
  margin-bottom: 16px;
  position: relative;
  &--photo {
    width: 160px;
    height: 128px;
    flex-grow: 0;
    position: relative;
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;
    .v-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate3d(-50%, -50%, 0px);
      cursor: pointer;
    }
    img {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      object-fit: cover;
    }
    &-loading {
      background: $bh-gray--6;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    &-clickable {
      cursor: pointer;
    }
    &-hover-loader {
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 1;
      transition: 0.2s linear !important;
      .v-progress-linear__background,
      .v-progress-linear__buffer,
      .v-progress-linear__determinate {
        transition: 0.2s linear !important;
      }
    }
  }
  &--desc {
    flex-grow: 1;
    display: flex;
    padding: 15px 17px;
    flex-direction: column;
    position: relative;
    justify-content: space-between;
    max-width: calc(100% - 160px);
    &-comments {
      display: flex;
      flex-direction: column;
      .comment {
        flex-grow: 0;
        align-self: flex-end;
        border-radius: 8px;
        background: $bh-blue--2;
        padding: 10px;
        min-width: 150px;
        max-width: 80%;
        margin-bottom: 5px;
        text-align: right;
        &.participants {
          align-self: flex-start;
          text-align: left;
          background: $bh-blue--4;
        }
        i {
          display: block;
          font-size: 0.8em;
        }
        span {
          display: block;
          max-width: 100%;
          word-wrap: break-word;
        }
      }
    }
    .reaction {
      position: absolute;
      right: 9px;
      top: 10px;
      &-wrapper {
        display: flex;
      }
    }
  }
  &-comment {
    position: relative;
    &--wrap {
      margin-top: 15px;
      p {
        margin-bottom: 0;
        img {
          width: 24px;
        }
        span {
          padding: 10px 13px;
          border-radius: 8px;
          font-size: 0.785715rem;
          margin-left: 8px;
          display: inline-block;
          vertical-align: top;
          background: $bh-white;
        }
        .v-icon {
          vertical-align: top;
          margin-top: 8px;
          margin-left: 10px;
          width: 20px;
          height: 20px;
          &::after {
            width: 30px;
            height: 30px;
            left: -5px;
            top: -3px;
            transform: scale(1);
          }
        }
      }
    }
    &--emoji {
      height: 100px;
      position: absolute;
      bottom: 50%;
      overflow-y: auto;
      padding: $gutter 0;
    }
    .v-input__append-outer {
      margin-top: 0 !important;
      margin-bottom: 0;
      align-self: center !important;
    }

    textarea {
      margin-top: 0 !important;
      padding-right: 0 !important;
      max-height: calc(1.75rem * 4);
      align-self: center;
      min-height: auto;
      overflow: visible;
    }

    .v-input__prepend-inner,
    .v-input__append-inner {
      margin-top: 0 !important;
      align-self: center !important;
    }
  }
}
</style>
